/* ============================================================================
 * skinAssets — resolve a master art path to the active skin's own compiled file
 * ----------------------------------------------------------------------------
 * Every desk crest and every status sprite ships locked to the MASTER skin's
 * palette. Wearing the master's file under a fork shows that fork a colour it
 * does not own — a School Immersion's dock was navy-and-blue on a green ground,
 * and under Poolsuite (a LIGHT ground, black ink) the whole set is simply wrong.
 *
 * scripts/skin-art.py compiles one full set per skin into
 * `public/skins/<skin>/{logos,pixel}/`, by ROLE, parsed out of that skin's own
 * CSS. This module is the read side: it maps a master path onto that set.
 *
 *   /logos/dock-embassy.png   ->  /skins/school/logos/dock-embassy.png
 *   /pixel/dock-live-tv.png   ->  /skins/school/pixel/dock-live-tv.png
 *
 * Anything else — the Supercompute wordmark, the owl, a franchisee's own mark —
 * is left alone on purpose. Which mark an Immersion wears is the FRANCHISEE's
 * field, so a skin must never be handed a recoloured version of someone else's
 * brand. See SkinDef.mark in src/hooks/useSkin.ts.
 * ========================================================================= */

import type { Skin } from '../hooks/useSkin';

/** The panel whose art IS the base art: it resolves to the files in /logos and
 * /pixel. After the consolidation, the base crests and sprites are Supercompute's
 * (navy/gold) — the design source's own art — so this stays `supercompute`. The
 * architectural master (oceanside) is a *different* concept: its compiled set
 * lives under /skins/oceanside/ like any other panel. */
export const BASE_ART_SKIN: Skin = 'supercompute';

const PREFIXES: ReadonlyArray<readonly [string, string]> = [
  ['/logos/', 'logos/'],
  ['/pixel/', 'pixel/'],
];

/** The active panel's compiled variant of a base art path. */
export function skinArt(path: string, skin: Skin): string {
  if (skin === BASE_ART_SKIN) return path;
  for (const [from, to] of PREFIXES) {
    if (path.startsWith(from)) {
      return `/skins/${skin}/${to}${path.slice(from.length)}`;
    }
  }
  return path;
}

/**
 * Every file the compiler emits for a skin. Kept in sync by
 * scripts/skin-art.py --check, which fails the build if a skin's set is short.
 */
export const SKIN_ART_PREFIX = '/skins';
